import React, { lazy } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { useUI } from '../../contexts/UIContext';
import { AppViewRouterProps } from './types';
import { isCommProject } from '../../utils/idNormalizer';

const GlobalCalendarView = lazy(() => import('../GlobalCalendarView'));
const MetricsDashboardView = lazy(() => import('../MetricsDashboardView'));
const AnnualReportView = lazy(() => import('../AnnualReportView'));
const ProjectsDashboardView = lazy(() => import('../ProjectsDashboardView'));
const IdeaPoolView = lazy(() => import('../IdeaPoolView'));

export const WorkspaceDashboardRouter: React.FC<AppViewRouterProps> = (props) => {
  const {
    workspaceProjects,
    activeCalendarTab,
    setActiveCalendarTab,
    otherWorkspaceProjects,
    calendarShowOtherWorkspace,
    setCalendarShowOtherWorkspace,
    calendarShowAllTasks,
    handleEditTaskGlobalMemo,
    todayStr,
    selectedMetricsProjectId,
    activeMetricsTab,
    setActiveMetricsTab,
    handleFilterTasksFromMetrics,
    hiddenProjectIds,
    setHiddenProjectIds,
    activePoolTab,
    setActivePoolTab,
  } = props;
  const { user } = useAuth();
  const projectCtx = useProject();
  const {
    allProjects,
    setCurrentProjectId,
    updateTask,
    updateProjectMetadata,
    globalMetadata,
    activeWorkspace,
    publishProjectToLibrary,
    importProjectFromLibrary,
    deleteProjectFromLibrary,
    fetchLibraryProjects,
    archiveProject,
    unarchiveProject,
    deleteProject,
    updateProjectCategory,
    shareProjectWithUser,
    unshareProjectWithUser,
  } = projectCtx;
  const scrollToTask = props.scrollToTask ?? props.ganttState?.scrollToTask;
  const handleDownloadRoadmap = props.handleDownloadRoadmap ?? projectCtx.handleDownloadRoadmap;

  const {
    viewMode,
    setViewMode,
    searchQuery,
    searchMode,
    searchMatchIds,
    activeSearchMatchId,
    showArchived,
    showCompleted,
    calendarDate,
    setCalendarDate,
    calendarViewType,
    setCalendarViewType,
    calendarFilterMode,
    isDarkMode,
    setEditingTask
  } = useUI();

  return (
    <>
      {/* Global Calendar / Dashboard */}
      {viewMode === 'dashboard' && (
        <GlobalCalendarView 
          projects={[
            ...workspaceProjects.filter(p => !isCommProject(p.id)),
            ...allProjects.filter(p => isCommProject(p.id))
          ]} 
          activeTab={activeCalendarTab}
          onActiveTabChange={setActiveCalendarTab}
          viewType={calendarViewType}
          onViewTypeChange={setCalendarViewType}
          currentDate={calendarDate}
          onCurrentDateChange={setCalendarDate}
          onSelectProject={setCurrentProjectId}
          onEditTask={handleEditTaskGlobalMemo}
          showAllTasks={calendarShowAllTasks}
          searchQuery={searchQuery}
          activeSearchMatchId={activeSearchMatchId}
          searchMatchIds={searchMatchIds}
          searchMode={searchMode}
          filterMode={calendarFilterMode}
          showCompleted={showCompleted}
          showArchived={showArchived}
          overlayProjects={otherWorkspaceProjects}
          activeWorkspace={activeWorkspace}
          showWorkspaceOverlay={calendarShowOtherWorkspace}
          onToggleWorkspaceOverlay={setCalendarShowOtherWorkspace}
          onJumpToGantt={(pid, tid) => {
            setCurrentProjectId(pid);
            setViewMode('gantt');
            setTimeout(() => scrollToTask(tid), 300);
          }}
        />
      )}

      {/* Metrics Dashboard View */}
      {viewMode === 'metricsDashboard' && (activeMetricsTab === 'overview' || activeMetricsTab === 'roadmap' || activeMetricsTab === 'time' || activeMetricsTab === 'activity') ? (
        <div style={{ height: '100%' }}>
          <MetricsDashboardView 
            projects={workspaceProjects} 
            todayStr={todayStr} 
            selectedProjectId={selectedMetricsProjectId}
            activeTab={activeMetricsTab as 'overview' | 'roadmap' | 'time' | 'activity'}
            onExportRoadmap={handleDownloadRoadmap}
            onTaskSelect={(task) => setEditingTask(task)}
            onFilterTasks={handleFilterTasksFromMetrics}
            updateTask={updateTask}
            hiddenProjectIds={hiddenProjectIds}
            setHiddenProjectIds={setHiddenProjectIds}
          />
        </div>
      ) : null}

      {/* Annual Report View */}
      {viewMode === 'metricsDashboard' && activeMetricsTab === 'annualReport' ? (
        <div style={{ height: '100%' }} className="print:!h-auto">
          <AnnualReportView
            projects={workspaceProjects}
            todayStr={todayStr}
            activeWorkspace={activeWorkspace}
            onUpdateProjectMetadata={updateProjectMetadata}
          />
        </div>
      ) : null}

      {/* Projects Dashboard View */}
      {((viewMode === 'metricsDashboard' && (activeMetricsTab === 'projects' || activeMetricsTab === 'library')) || (viewMode === 'projectPool' && (activePoolTab === 'activeProjects' || activePoolTab === 'library' || activePoolTab === 'archive'))) ? (
        <div style={{ height: '100%' }}>
          <ProjectsDashboardView
            projects={workspaceProjects}
            todayStr={todayStr}
            isDarkMode={isDarkMode}
            globalMetadata={globalMetadata}
            onUpdateGlobalMetadata={(updates) => updateProjectMetadata('inbox_inforum', updates)}
            activeWorkspace={activeWorkspace}
            currentUserEmail={user?.email ?? null}
            onSelectProject={(id) => { setCurrentProjectId(id); setViewMode('gantt'); }}
            onSwitchViewMode={setViewMode}
            publishProjectToLibrary={publishProjectToLibrary}
            importProjectFromLibrary={importProjectFromLibrary}
            deleteProjectFromLibrary={deleteProjectFromLibrary}
            fetchLibraryProjects={fetchLibraryProjects}
            archiveProject={archiveProject}
            unarchiveProject={unarchiveProject}
            deleteProject={deleteProject}
            searchQuery={searchQuery}
            searchMatchIds={searchMatchIds}
            viewMode={viewMode}
            activeTab={viewMode === 'projectPool' ? (activePoolTab === 'library' ? 'library' : activePoolTab === 'archive' ? 'archived-projects' : 'my-projects') : (activeMetricsTab === 'library' ? 'library' : 'my-projects')}
            onChangeActiveTab={(tab) => {
              if (viewMode === 'projectPool') {
                setActivePoolTab(tab === 'library' ? 'library' : tab === 'archived-projects' ? 'archive' : 'activeProjects');
              } else {
                setActiveMetricsTab(tab === 'library' ? 'library' : 'projects');
              }
            }}
            onAssignCategory={updateProjectCategory}
            onUpdateProjectMetadata={updateProjectMetadata}
            shareProjectWithUser={shareProjectWithUser}
            unshareProjectWithUser={unshareProjectWithUser}
            currentUserId={user?.uid ?? null}
          />
        </div>
      ) : null}

      {/* Idea Pool View */}
      {((viewMode === 'projectPool' && activePoolTab === 'ideaPool') || (viewMode === 'metricsDashboard' && activeMetricsTab === 'ideaPool')) ? (
        <div style={{ height: '100%' }}>
          {user?.uid && (
            <IdeaPoolView 
              userId={user.uid} 
              activeWorkspace={activeWorkspace} 
              onNavigateToProject={(projId) => {
                setCurrentProjectId(projId);
                setViewMode('gantt');
              }}
            />
          )}
        </div>
      ) : null}
    </>
  );
};

export default WorkspaceDashboardRouter;
